import { IconPhotoPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { QuestionImage } from '../../lib/question-blocks';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { offlineSessionWorkerUrl } from '../../lib/worker-urls';
import {
  INFORMATION_BLOCK_ALLOWED_MIME_TYPES,
  INFORMATION_BLOCK_MAX_BYTES,
  INFORMATION_BLOCK_MIME_REJECT_MESSAGE,
  informationBlockSizeRejectMessage,
  isAllowedInformationBlockMime,
} from '../../lib/information-blocks/constants';

/** Props for the shared question-image authoring control. */
export interface QuestionImageEditorProps {
  image?: QuestionImage;
  label: string;
  workspaceId?: string;
  committedImageKeys?: ReadonlySet<string>;
  /** Preserve the full image for coordinate-sensitive questions. */
  fit?: 'cover' | 'contain';
  onChange: (image: QuestionImage | undefined) => void;
}

interface UploadResponse {
  imageStorageKey: string;
  imageUrl: string;
}

interface CaptionResponse {
  title?: string;
  caption?: string;
}

const accept = INFORMATION_BLOCK_ALLOWED_MIME_TYPES.join(',');
const focus = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';

/** Reuses the information-block media pipeline without persisting temporary URLs. */
export function QuestionImageEditor({ image, label, workspaceId, committedImageKeys, fit = 'cover',
  onChange }: QuestionImageEditorProps): React.ReactElement {
  const { supabase } = useSupabase();
  const inputRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef<AbortController | null>(null);
  const imageRef = useRef(image);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedFile, setFailedFile] = useState<File | null>(null);
  const [broken, setBroken] = useState(false);
  imageRef.current = image;

  useEffect(() => () => requestRef.current?.abort(), []);

  const token = useCallback(async (): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    return session.access_token;
  }, [supabase]);

  const deleteDraft = useCallback(async (imageStorageKey: string): Promise<void> => {
    if (committedImageKeys?.has(imageStorageKey)) return;
    try {
      const authorization = await token();
      const response = await fetch(`${offlineSessionWorkerUrl()}/information-block/delete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authorization}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId, imageStorageKey }),
      });
      if (!response.ok) throw new Error(`Image deletion failed (${response.status})`);
    } catch (caught) {
      captureException(caught instanceof Error ? caught : new Error(String(caught)), 'QuestionImageEditor.deleteDraft', { workspaceId });
    }
  }, [committedImageKeys, token, workspaceId]);

  const caption = useCallback(async (next: QuestionImage, signal: AbortSignal): Promise<void> => {
    try {
      const authorization = await token();
      const response = await fetch(`${offlineSessionWorkerUrl()}/information-block/caption`, {
        method: 'POST', signal,
        headers: { Authorization: `Bearer ${authorization}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspaceId, imageStorageKey: next.imageStorageKey, hasUserTitle: false, hasUserDirective: true }),
      });
      if (!response.ok) throw new Error(`Caption failed (${response.status})`);
      const result = await response.json() as CaptionResponse;
      const latest = imageRef.current;
      if (signal.aborted || latest?.imageStorageKey !== next.imageStorageKey) return;
      const generatedAlt = result.title?.trim() || result.caption?.trim() || next.alt;
      const resolved = {
        ...latest,
        alt: latest.alt === next.alt ? generatedAlt : latest.alt,
        ...(result.caption?.trim() ? { caption: result.caption.trim() } : {}),
      };
      imageRef.current = resolved;
      onChange(resolved);
    } catch (caught) {
      if (signal.aborted) return;
      captureException(caught instanceof Error ? caught : new Error(String(caught)), 'QuestionImageEditor.caption', { workspaceId });
      setError('Caption failed. Add alt text');
    }
  }, [onChange, token, workspaceId]);

  const upload = useCallback(async (file: File): Promise<void> => {
    setError(null);
    if (!isAllowedInformationBlockMime(file.type)) {
      setError(INFORMATION_BLOCK_MIME_REJECT_MESSAGE);
      return;
    }
    if (file.size > INFORMATION_BLOCK_MAX_BYTES) {
      setError(informationBlockSizeRejectMessage());
      return;
    }
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(true);
    setFailedFile(null);
    try {
      const authorization = await token();
      const body = new FormData();
      body.append('file', file);
      if (workspaceId) body.append('workspaceId', workspaceId);
      const response = await fetch(`${offlineSessionWorkerUrl()}/information-block/upload`, {
        method: 'POST', signal: controller.signal,
        headers: { Authorization: `Bearer ${authorization}` }, body,
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status})`);
      const result = await response.json() as UploadResponse;
      if (controller.signal.aborted) {
        void deleteDraft(result.imageStorageKey);
        return;
      }
      const previous = imageRef.current;
      const next: QuestionImage = {
        imageStorageKey: result.imageStorageKey,
        imageUrl: result.imageUrl,
        alt: file.name,
      };
      imageRef.current = next;
      onChange(next);
      setBroken(false);
      if (previous?.imageStorageKey && previous.imageStorageKey !== next.imageStorageKey) void deleteDraft(previous.imageStorageKey);
      await caption(next, controller.signal);
    } catch (caught) {
      if (controller.signal.aborted) return;
      captureException(caught instanceof Error ? caught : new Error(String(caught)), 'QuestionImageEditor.upload', { workspaceId });
      setFailedFile(file);
      setError('Upload failed');
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setBusy(false);
      }
    }
  }, [caption, deleteDraft, onChange, token, workspaceId]);

  const remove = (): void => {
    requestRef.current?.abort();
    requestRef.current = null;
    setBusy(false);
    setFailedFile(null);
    setError(null);
    setBroken(false);
    const previous = imageRef.current;
    imageRef.current = undefined;
    onChange(undefined);
    if (previous?.imageStorageKey) void deleteDraft(previous.imageStorageKey);
  };

  return <div className="space-y-2">
    <input ref={inputRef} type="file" accept={accept} aria-label={label} hidden
      onChange={event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) void upload(file);
      }} />
    {image && !broken ? <div className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-surface">
      <button type="button" aria-label="Replace image" className={`${focus} block h-full w-full cursor-pointer`}
        onClick={() => inputRef.current?.click()}>
        <img src={image.imageUrl} alt={image.alt} width={4} height={3} loading="lazy"
          className={`h-full w-full ${fit === 'contain' ? 'object-contain' : 'object-cover'}`} onError={() => setBroken(true)} />
      </button>
      <button type="button" aria-label="Remove image" className={`${focus} absolute right-2 top-2 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg bg-ink/80 text-white opacity-100 transition-opacity motion-reduce:transition-none sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus:opacity-100`}
        onClick={remove}><IconTrash aria-hidden="true" className="h-4 w-4" /></button>
    </div> : <button type="button" aria-label={image && broken ? 'Replace image' : 'Add image'}
      className={`${focus} flex aspect-[4/3] w-full cursor-pointer items-center justify-center rounded-lg border border-dashed border-border bg-background text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-surface-hover hover:text-foreground`}
      onClick={() => inputRef.current?.click()}><IconPhotoPlus aria-hidden="true" className="h-6 w-6" /></button>}
    {busy ? <div className="flex items-center justify-between gap-2">
      <span role="status" aria-label="Uploading" className="h-4 w-4 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground motion-reduce:animate-none" />
      <button type="button" className={`${focus} cursor-pointer text-sm text-muted-foreground underline`} onClick={remove}>Remove image</button>
    </div> : null}
    {error ? <div role="alert" className="flex items-center justify-between gap-2 text-sm text-danger">
      <span>{error}</span>
      {failedFile ? <button type="button" aria-label="Retry upload" className={`${focus} cursor-pointer rounded-lg p-2 hover:bg-surface-hover`}
        onClick={() => void upload(failedFile)}><IconRefresh aria-hidden="true" className="h-4 w-4" /></button> : null}
    </div> : null}
    {image ? <input aria-label={`${label} alt text`} value={image.alt} className={`${focus} min-h-8 w-full rounded-lg bg-transparent px-2 py-1 text-sm text-muted-foreground transition-colors motion-reduce:transition-none hover:bg-surface-hover hover:text-foreground focus-visible:text-foreground`}
      onChange={event => {
        const next = { ...image, alt: event.target.value };
        imageRef.current = next;
        onChange(next);
      }} /> : null}
  </div>;
}
